import { Card } from "@pericles/framework-frontend";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  applyFoundryV2RolloutAction,
  createFoundryV2RolloutState,
  createFoundryV2GovernanceApproval,
  createFoundryV2RolloutWave,
  fetchFoundryV2GovernanceConsole,
  fetchFoundryV2PortfolioReleaseBoard,
  fetchLatestFoundryV2GovernanceApproval,
  listFoundryV2GovernanceApprovals,
  type FoundryV2GovernanceConsoleRecord,
  type FoundryV2TenantRolloutAction,
  type FoundryV2TenantRolloutControlRecord,
  type FoundryV2CutoverDashboardRecord,
  type FoundryV2ExecutionRunRecord,
  type FoundryV2GovernanceApprovalDecision,
  type FoundryV2GovernanceApprovalRecord,
  type FoundryV2HandoffRecord,
  type FoundryV2PortfolioReleaseBoardRecord,
  type FoundryV2ReadinessPackRecord,
  type FoundryV2ReleaseBundleRecord,
  type FoundryV2RolloutWaveOutcome,
  type FoundryV2RolloutWavePhase,
  type FoundryV2RolloutWaveStatus,
  type FoundryV2TenantRolloutMode,
  type FoundryV2TenantRolloutStateRecord,
} from "../api/foundryV2Client";
import { FoundryV2AccessGuard } from "./FoundryV2AccessGuard";
import { Button } from "./ui";

const CONTROL_ROWS: Array<{
  control: string;
  status: "active" | "pending";
  detail: string;
}> = [
  {
    control: "Risk-tiered approvals",
    status: "active",
    detail: "Persists explicit approval decisions with approver and evidence reference.",
  },
  {
    control: "High-risk run targeting",
    status: "active",
    detail: "Scopes approvals per Foundry V2 run and tracks targeted high-risk stages.",
  },
  {
    control: "Decision audit trail",
    status: "active",
    detail: "Stores approval history per handoff for governance evidence replay.",
  },
  {
    control: "SLA escalation timers",
    status: "active",
    detail: "Per-approval deadline, countdown, and escalation status are computed and surfaced live.",
  },
];

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms)) return "-";
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function formatDateTime(value: string | null): string {
  if (!value) return "-";
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return value;
  return new Date(parsed).toLocaleString();
}

function approvalStatusBadgeClass(status: FoundryV2GovernanceApprovalRecord["status"]): string {
  if (status === "approved") return "bg-emerald-100 text-emerald-700";
  if (status === "rejected") return "bg-red-100 text-red-700";
  return "bg-amber-100 text-amber-700";
}

function escalationBadgeClass(escalation: FoundryV2GovernanceApprovalRecord["escalation"]): string {
  if (escalation === "escalated") return "bg-red-100 text-red-700";
  if (escalation === "breached") return "bg-orange-100 text-orange-700";
  if (escalation === "watch") return "bg-amber-100 text-amber-700";
  return "bg-slate-100 text-slate-700";
}

function readinessDecisionBadgeClass(decision: FoundryV2ReadinessPackRecord["summary"]["decision"]): string {
  if (decision === "GO") return "bg-emerald-100 text-emerald-700";
  if (decision === "WATCH") return "bg-amber-100 text-amber-700";
  return "bg-red-100 text-red-700";
}

function checklistStatusBadgeClass(status: FoundryV2ReadinessPackRecord["checklist"][number]["status"]): string {
  if (status === "pass") return "bg-emerald-100 text-emerald-700";
  if (status === "watch") return "bg-amber-100 text-amber-700";
  return "bg-red-100 text-red-700";
}

function cutoverDecisionBadgeClass(decision: FoundryV2CutoverDashboardRecord["summary"]["decision"]): string {
  if (decision === "PROMOTE_DEFAULT" || decision === "PROMOTE_STAGED" || decision === "PROMOTE_PILOT") {
    return "bg-emerald-100 text-emerald-700";
  }
  if (decision === "HOLD") return "bg-amber-100 text-amber-700";
  return "bg-red-100 text-red-700";
}

function cutoverGateBadgeClass(status: FoundryV2CutoverDashboardRecord["gates"][number]["status"]): string {
  if (status === "pass") return "bg-emerald-100 text-emerald-700";
  if (status === "watch") return "bg-amber-100 text-amber-700";
  return "bg-red-100 text-red-700";
}

function rolloutOutcomeBadgeClass(outcome: FoundryV2RolloutWaveOutcome): string {
  if (outcome === "stable") return "bg-emerald-100 text-emerald-700";
  if (outcome === "watch") return "bg-amber-100 text-amber-700";
  return "bg-red-100 text-red-700";
}

function rolloutStatusBadgeClass(status: FoundryV2RolloutWaveStatus): string {
  if (status === "completed") return "bg-emerald-100 text-emerald-700";
  if (status === "monitoring") return "bg-blue-100 text-blue-700";
  if (status === "planned") return "bg-slate-100 text-slate-700";
  return "bg-red-100 text-red-700";
}

function tenantRolloutModeBadgeClass(mode: FoundryV2TenantRolloutStateRecord["mode"]): string {
  if (mode === "default") return "bg-emerald-100 text-emerald-700";
  if (mode === "staged") return "bg-blue-100 text-blue-700";
  if (mode === "pilot") return "bg-amber-100 text-amber-700";
  if (mode === "legacy-fallback-only") return "bg-orange-100 text-orange-700";
  return "bg-slate-100 text-slate-700";
}

function releaseStatusBadgeClass(status: FoundryV2ReleaseBundleRecord["summary"]["releaseStatus"]): string {
  if (status === "ready") return "bg-emerald-100 text-emerald-700";
  if (status === "watch") return "bg-amber-100 text-amber-700";
  return "bg-red-100 text-red-700";
}

function portfolioReleaseStatusBadgeClass(
  status: FoundryV2PortfolioReleaseBoardRecord["items"][number]["releaseStatus"],
): string {
  if (status === "ready") return "bg-emerald-100 text-emerald-700";
  if (status === "watch") return "bg-amber-100 text-amber-700";
  if (status === "blocked") return "bg-red-100 text-red-700";
  return "bg-slate-100 text-slate-700";
}

function resolveLiveSla(
  approval: FoundryV2GovernanceApprovalRecord,
  nowMs: number,
): { remainingMs: number; overdueMs: number } {
  if (approval.status !== "pending") {
    return {
      remainingMs: Math.max(0, approval.remainingMs),
      overdueMs: Math.max(0, approval.overdueMs),
    };
  }

  const deadlineMs = Date.parse(approval.deadlineAt);
  if (!Number.isFinite(deadlineMs)) {
    return {
      remainingMs: Math.max(0, approval.remainingMs),
      overdueMs: Math.max(0, approval.overdueMs),
    };
  }

  return {
    remainingMs: Math.max(0, deadlineMs - nowMs),
    overdueMs: Math.max(0, nowMs - deadlineMs),
  };
}

function resolveProjectId(search: string): string {
  const params = new URLSearchParams(search);
  const fromQuery = params.get("projectId");
  if (fromQuery?.trim()) return fromQuery.trim();
  if (typeof window === "undefined") return "";
  try {
    const raw = window.localStorage.getItem("longwalls.v2.handoffPayload");
    if (!raw) return "";
    const parsed = JSON.parse(raw) as { projectId?: string };
    return parsed.projectId?.trim() ?? "";
  } catch {
    return "";
  }
}

function firstGovernanceConsoleError(
  errors: FoundryV2GovernanceConsoleRecord["errors"] | null | undefined,
): string | null {
  if (!errors) return null;
  const orderedKeys: Array<keyof FoundryV2GovernanceConsoleRecord["errors"]> = [
    "runs",
    "approvals",
    "readinessPack",
    "cutoverDashboard",
    "rolloutState",
    "rolloutControl",
    "releaseBundle",
  ];
  for (const key of orderedKeys) {
    const message = errors[key];
    if (typeof message === "string" && message.trim()) {
      return message;
    }
  }
  return null;
}

export function GovernanceV2Screen(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const projectId = useMemo(() => resolveProjectId(location.search), [location.search]);

  const [portfolioBoard, setPortfolioBoard] = useState<FoundryV2PortfolioReleaseBoardRecord | null>(null);
  const [portfolioBoardError, setPortfolioBoardError] = useState<string | null>(null);
  const [latestHandoff, setLatestHandoff] = useState<FoundryV2HandoffRecord | null>(null);
  const [runs, setRuns] = useState<FoundryV2ExecutionRunRecord[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string>("");
  const [latestApproval, setLatestApproval] = useState<FoundryV2GovernanceApprovalRecord | null>(null);
  const [approvalHistory, setApprovalHistory] = useState<FoundryV2GovernanceApprovalRecord[]>([]);
  const [readinessPack, setReadinessPack] = useState<FoundryV2ReadinessPackRecord | null>(null);
  const [cutoverDashboard, setCutoverDashboard] = useState<FoundryV2CutoverDashboardRecord | null>(null);
  const [rolloutState, setRolloutState] = useState<FoundryV2TenantRolloutStateRecord | null>(null);
  const [rolloutControl, setRolloutControl] = useState<FoundryV2TenantRolloutControlRecord | null>(null);
  const [releaseBundle, setReleaseBundle] = useState<FoundryV2ReleaseBundleRecord | null>(null);
  const [approvalSnapshotRunId, setApprovalSnapshotRunId] = useState("");
  const [decision, setDecision] = useState<FoundryV2GovernanceApprovalDecision>("approved");
  const [approver, setApprover] = useState("");
  const [evidenceRef, setEvidenceRef] = useState("");
  const [note, setNote] = useState("");
  const [rolloutPhase, setRolloutPhase] = useState<FoundryV2RolloutWavePhase>("pilot");
  const [rolloutStatus, setRolloutStatus] = useState<FoundryV2RolloutWaveStatus>("completed");
  const [rolloutOutcome, setRolloutOutcome] = useState<FoundryV2RolloutWaveOutcome>("stable");
  const [tenantRolloutMode, setTenantRolloutMode] = useState<FoundryV2TenantRolloutMode>("pilot");
  const [tenantRolloutDecider, setTenantRolloutDecider] = useState("");
  const [tenantRolloutFallbackEnabled, setTenantRolloutFallbackEnabled] = useState(true);
  const [tenantRolloutEvidenceRefs, setTenantRolloutEvidenceRefs] = useState("");
  const [tenantRolloutNote, setTenantRolloutNote] = useState("");
  const [rolloutLabel, setRolloutLabel] = useState("");
  const [rolloutTenants, setRolloutTenants] = useState("");
  const [rolloutEvidenceRefs, setRolloutEvidenceRefs] = useState("");
  const [rolloutNote, setRolloutNote] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isPortfolioLoading, setIsPortfolioLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [clockMs, setClockMs] = useState(() => Date.now());

  const selectedRun = useMemo(
    () => runs.find((run) => run.id === selectedRunId) ?? null,
    [runs, selectedRunId],
  );

  const pendingHighRiskRuns = useMemo(
    () => runs.filter((run) => run.approval.required && !run.approval.approved),
    [runs],
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      setClockMs(Date.now());
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!projectId) {
      setPortfolioBoard(null);
      setPortfolioBoardError(null);
      setLatestHandoff(null);
      setRuns([]);
      setSelectedRunId("");
      setApprovalSnapshotRunId("");
      setLatestApproval(null);
      setApprovalHistory([]);
      setReadinessPack(null);
      setCutoverDashboard(null);
      setRolloutState(null);
      setRolloutControl(null);
      setReleaseBundle(null);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    void fetchFoundryV2GovernanceConsole(projectId)
      .then((snapshot) => {
        setPortfolioBoard(snapshot.portfolioBoard);
        setPortfolioBoardError(snapshot.errors.portfolioBoard);
        setLatestHandoff(snapshot.latestHandoff);
        setRuns(snapshot.runs);
        setSelectedRunId(snapshot.selectedRunId ?? "");
        setApprovalSnapshotRunId(snapshot.selectedRunId ?? "");
        setLatestApproval(snapshot.latestApproval);
        setApprovalHistory(snapshot.approvalHistory);
        setReadinessPack(snapshot.readinessPack);
        setCutoverDashboard(snapshot.cutoverDashboard);
        setRolloutState(snapshot.rolloutState);
        setRolloutControl(snapshot.rolloutControl);
        setReleaseBundle(snapshot.releaseBundle);
        setError(firstGovernanceConsoleError(snapshot.errors));
      })
      .catch((cause) => {
        const message = cause instanceof Error ? cause.message : "Failed to load the Foundry V2 governance console";
        setPortfolioBoard(null);
        setPortfolioBoardError(null);
        setLatestHandoff(null);
        setRuns([]);
        setSelectedRunId("");
        setApprovalSnapshotRunId("");
        setLatestApproval(null);
        setApprovalHistory([]);
        setReadinessPack(null);
        setCutoverDashboard(null);
        setRolloutState(null);
        setRolloutControl(null);
        setReleaseBundle(null);
        setError(message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [projectId]);

  useEffect(() => {
    if (!projectId) {
      setPortfolioBoard(null);
      setPortfolioBoardError(null);
      setIsPortfolioLoading(false);
      return;
    }

    let cancelled = false;
    setIsPortfolioLoading(true);
    setPortfolioBoardError(null);

    void fetchFoundryV2PortfolioReleaseBoard()
      .then((board) => {
        if (cancelled) return;
        setPortfolioBoard(board);
      })
      .catch((cause) => {
        if (cancelled) return;
        const message =
          cause instanceof Error ? cause.message : "Failed to load the tenant release command center";
        setPortfolioBoard(null);
        setPortfolioBoardError(message);
      })
      .finally(() => {
        if (cancelled) return;
        setIsPortfolioLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (!projectId || !latestHandoff?.id) {
      setLatestApproval(null);
      setApprovalHistory([]);
      setApprovalSnapshotRunId("");
      return;
    }
    if (!selectedRunId) {
      setLatestApproval(null);
      setApprovalHistory([]);
      setApprovalSnapshotRunId("");
      return;
    }
    if (selectedRunId === approvalSnapshotRunId) {
      return;
    }

    const options = selectedRunId ? { runId: selectedRunId } : {};
    setIsLoading(true);
    setError(null);
    void Promise.all([
      fetchLatestFoundryV2GovernanceApproval(projectId, latestHandoff.id, options),
      listFoundryV2GovernanceApprovals(projectId, latestHandoff.id, options),
    ])
      .then(([approval, approvals]) => {
        setLatestApproval(approval);
        setApprovalHistory(approvals);
        setApprovalSnapshotRunId(selectedRunId);
        if (approval) {
          if (approval.decision) {
            setDecision(approval.decision);
          } else {
            setDecision("approved");
          }
          setApprover(approval.approver ?? "");
          setEvidenceRef(approval.evidenceRef ?? "");
          setNote(approval.note ?? "");
        } else {
          setDecision("approved");
          setApprover("");
          setEvidenceRef("");
          setNote("");
        }
      })
      .catch((cause) => {
        const message = cause instanceof Error ? cause.message : "Failed to load Governance V2 approvals";
        setLatestApproval(null);
        setApprovalHistory([]);
        setError(message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [approvalSnapshotRunId, latestHandoff?.id, projectId, selectedRunId]);

  useEffect(() => {
    if (!rolloutState) {
      setTenantRolloutMode("pilot");
      setTenantRolloutDecider("");
      setTenantRolloutFallbackEnabled(true);
      setTenantRolloutEvidenceRefs("");
      setTenantRolloutNote("");
      return;
    }

    setTenantRolloutMode(rolloutState.mode);
    setTenantRolloutDecider(rolloutState.decidedBy ?? "");
    setTenantRolloutFallbackEnabled(rolloutState.legacyFallbackEnabled);
    setTenantRolloutEvidenceRefs(rolloutState.evidenceRefs.join(", "));
    setTenantRolloutNote(rolloutState.note ?? "");
  }, [rolloutState]);

  const refreshGovernanceConsole = async (preferredRunId?: string): Promise<void> => {
    if (!projectId) return;
    const snapshot = await fetchFoundryV2GovernanceConsole(projectId, {
      runId: preferredRunId?.trim() || undefined,
    });
    setPortfolioBoard(snapshot.portfolioBoard);
    setPortfolioBoardError(snapshot.errors.portfolioBoard);
    setLatestHandoff(snapshot.latestHandoff);
    setRuns(snapshot.runs);
    setSelectedRunId(snapshot.selectedRunId ?? "");
    setApprovalSnapshotRunId(snapshot.selectedRunId ?? "");
    setLatestApproval(snapshot.latestApproval);
    setApprovalHistory(snapshot.approvalHistory);
    setReadinessPack(snapshot.readinessPack);
    setCutoverDashboard(snapshot.cutoverDashboard);
    setRolloutState(snapshot.rolloutState);
    setRolloutControl(snapshot.rolloutControl);
    setReleaseBundle(snapshot.releaseBundle);
    setError(firstGovernanceConsoleError(snapshot.errors));
  };

  const submitApproval = async (): Promise<void> => {
    if (!projectId || !latestHandoff?.id || !selectedRun?.id) {
      setError("Select a project, handoff, and run before submitting governance approval.");
      return;
    }
    if (!approver.trim()) {
      setError("Approver is required.");
      return;
    }
    if (decision === "approved" && !evidenceRef.trim()) {
      setError("Evidence reference is required when decision is approved.");
      return;
    }

    setError(null);
    setSuccess(null);
    setIsSubmitting(true);
    try {
      const approval = await createFoundryV2GovernanceApproval(projectId, latestHandoff.id, {
        runId: selectedRun.id,
        decision,
        approver: approver.trim(),
        evidenceRef: evidenceRef.trim() || undefined,
        note: note.trim() || undefined,
      });
      await refreshGovernanceConsole(selectedRun.id);
      setLatestApproval(approval);
      setSuccess(`Decision ${(approval.decision ?? approval.status).toUpperCase()} recorded for run ${approval.runId}.`);
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : "Failed to record governance approval";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitRolloutWave = async (): Promise<void> => {
    if (!projectId) {
      setError("Select a project before recording a rollout review window.");
      return;
    }

    setError(null);
    setSuccess(null);
    setIsSubmitting(true);
    try {
      const evidenceRefs = rolloutEvidenceRefs
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);
      const tenantIds = rolloutTenants
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);

      const wave = await createFoundryV2RolloutWave(projectId, {
        phase: rolloutPhase,
        status: rolloutStatus,
        outcome: rolloutOutcome,
        reviewWindowLabel: rolloutLabel.trim() || undefined,
        evidenceRefs,
        tenantIds,
        note: rolloutNote.trim() || undefined,
      });
      await refreshGovernanceConsole(selectedRunId);
      setRolloutLabel("");
      setRolloutTenants("");
      setRolloutEvidenceRefs("");
      setRolloutNote("");
      setSuccess(`Rollout review window ${wave.reviewWindowLabel} recorded for ${wave.phase} phase.`);
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : "Failed to record rollout review window";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitTenantRolloutState = async (): Promise<void> => {
    if (!projectId) {
      setError("Select a project before updating tenant rollout state.");
      return;
    }
    if (!tenantRolloutDecider.trim()) {
      setError("Operator is required before applying a rollout override.");
      return;
    }

    setError(null);
    setSuccess(null);
    setIsSubmitting(true);
    try {
      const evidenceRefs = tenantRolloutEvidenceRefs
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);
      if (!evidenceRefs.length) {
        throw new Error("At least one evidence reference is required before applying a rollout override.");
      }

      const rollout = await createFoundryV2RolloutState(projectId, {
        mode: tenantRolloutMode,
        legacyFallbackEnabled: tenantRolloutFallbackEnabled,
        decidedBy: tenantRolloutDecider.trim() || undefined,
        evidenceRefs,
        note: tenantRolloutNote.trim() || undefined,
        sourceWaveId: cutoverDashboard?.rolloutHistory.waves[0]?.id,
      });

      await refreshGovernanceConsole(selectedRunId);
      setSuccess(`Tenant rollout state is now ${rollout.mode}.`);
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : "Failed to update tenant rollout state";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitRolloutAction = async (action: FoundryV2TenantRolloutAction): Promise<void> => {
    if (!projectId) {
      setError("Select a project before applying a rollout action.");
      return;
    }

    setError(null);
    setSuccess(null);
    setIsSubmitting(true);
    try {
      const evidenceRefs = tenantRolloutEvidenceRefs
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);
      const result = await applyFoundryV2RolloutAction(projectId, {
        action,
        decidedBy: tenantRolloutDecider.trim() || undefined,
        evidenceRefs,
        note: tenantRolloutNote.trim() || undefined,
        sourceWaveId: cutoverDashboard?.rolloutHistory.waves[0]?.id,
      });

      setRolloutState(result.rolloutState);
      setRolloutControl(result.rolloutControl);
      await refreshGovernanceConsole(selectedRunId);
      setSuccess(`${result.transition.action} recorded: ${result.transition.previousMode} -> ${result.transition.nextMode}.`);
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : "Failed to apply tenant rollout action";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const operatorEvidenceRefs = tenantRolloutEvidenceRefs
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);
  const operatorActionDisabled = isSubmitting || isLoading || !tenantRolloutDecider.trim() || operatorEvidenceRefs.length === 0;

  return (
    <FoundryV2AccessGuard projectId={projectId} target="governance-v2">
      <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6">
      <div className="rounded-xl border border-slate-200 bg-white/80 px-4 py-4 shadow-sm">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Governance V2</h1>
        <p className="mt-2 text-sm text-slate-600">
          Dedicated approval workflow for Foundry/Jarvis V2 high-risk stages.
        </p>
      </div>

      {portfolioBoard ? (
        <Card className="border border-slate-200" bodyClassName="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Portfolio command center</div>
              <div className="text-2xl font-semibold text-slate-900">{portfolioBoard.summary.headline}</div>
              <p className="max-w-3xl text-sm text-slate-600">
                Tenant-level release posture across active LongWalls projects, with promotion pressure and fallback risk visible at a glance.
              </p>
            </div>
            <div className="grid gap-2 text-right text-xs text-slate-500 sm:grid-cols-2">
              <div>Total projects: {portfolioBoard.summary.totalProjects}</div>
              <div>V2 started: {portfolioBoard.summary.v2Started}</div>
              <div>Ready: {portfolioBoard.summary.ready}</div>
              <div>Watch: {portfolioBoard.summary.watch}</div>
              <div>Blocked: {portfolioBoard.summary.blocked}</div>
              <div>Not started: {portfolioBoard.summary.notStarted}</div>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
            <div className="rounded border border-slate-200 bg-slate-50 p-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Default mode</div>
              <div className="mt-2 text-2xl font-semibold text-slate-900">{portfolioBoard.summary.defaultMode}</div>
            </div>
            <div className="rounded border border-slate-200 bg-slate-50 p-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Fallback-only</div>
              <div className="mt-2 text-2xl font-semibold text-slate-900">{portfolioBoard.summary.fallbackOnly}</div>
            </div>
            {portfolioBoard.summary.focusAreas.slice(0, 4).map((focus) => (
              <div key={focus} className="rounded border border-slate-200 bg-white p-3 text-xs text-slate-700">
                {focus}
              </div>
            ))}
          </div>

          <div className="grid gap-3 xl:grid-cols-2">
            {portfolioBoard.items.slice(0, 6).map((item) => {
              const isCurrent = item.projectId === projectId;
              return (
                <div
                  key={item.projectId}
                  className={`rounded border p-4 ${
                    isCurrent ? "border-blue-300 bg-blue-50/40" : "border-slate-200 bg-slate-50"
                  }`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="text-sm font-semibold text-slate-900">{item.projectName}</div>
                        {isCurrent ? (
                          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700">
                            CURRENT
                          </span>
                        ) : null}
                      </div>
                      <div className="text-xs text-slate-500">
                        {item.projectCode} {item.customerName ? `• ${item.customerName}` : ""}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${portfolioReleaseStatusBadgeClass(item.releaseStatus)}`}>
                        {item.releaseStatus.replace("_", " ").toUpperCase()}
                      </span>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tenantRolloutModeBadgeClass(item.rolloutMode)}`}>
                        {item.rolloutMode.toUpperCase()}
                      </span>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${cutoverDecisionBadgeClass(item.cutoverDecision)}`}>
                        {item.cutoverDecision}
                      </span>
                    </div>
                  </div>

                  <div className="mt-3 grid gap-2 text-xs text-slate-700 sm:grid-cols-2">
                    <div>Confidence score: {item.confidenceScore}</div>
                    <div>Active path: {item.activePath}</div>
                    <div>Pending approvals: {item.pendingApprovals}</div>
                    <div>High-risk open: {item.highRiskOpen}</div>
                    <div>
                      Stable windows: {item.stableReviewWindows}/{item.requiredStableReviewWindows}
                    </div>
                    <div>Latest run: {item.latestRunId ?? "-"}</div>
                  </div>

                  <div className="mt-3 text-sm text-slate-700">{item.headline}</div>
                  <div className="mt-2 text-xs text-slate-600">Next action: {item.nextAction}</div>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {!isCurrent ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          navigate(`/governance-v2?projectId=${encodeURIComponent(item.projectId)}`)
                        }
                      >
                        Open project
                      </Button>
                    ) : null}
                    {item.appName ? (
                      <span className="text-[11px] text-slate-500">App: {item.appName}</span>
                    ) : (
                      <span className="text-[11px] text-slate-500">App handoff not started yet</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      ) : isPortfolioLoading ? (
        <Card className="border border-slate-200" bodyClassName="space-y-2 p-4">
          <div className="text-sm font-semibold text-slate-900">Portfolio command center</div>
          <div className="text-sm text-slate-600">
            Loading tenant-wide rollout posture without blocking the current project governance view.
          </div>
        </Card>
      ) : portfolioBoardError ? (
        <Card className="border border-amber-200" bodyClassName="space-y-2 p-4">
          <div className="text-sm font-semibold text-slate-900">Portfolio command center unavailable</div>
          <div className="text-sm text-slate-700">{portfolioBoardError}</div>
        </Card>
      ) : null}

      {projectId && readinessPack ? (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(320px,0.9fr)]">
          <Card className="border border-slate-200" bodyClassName="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-1">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">JV2-H4 readiness pack</div>
                <div className="text-2xl font-semibold text-slate-900">{readinessPack.summary.headline}</div>
                <p className="max-w-3xl text-sm text-slate-600">{readinessPack.summary.rationale}</p>
              </div>
              <div className="flex flex-col items-end gap-2 text-right">
                <span
                  className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${readinessDecisionBadgeClass(
                    readinessPack.summary.decision,
                  )}`}
                >
                  {readinessPack.summary.decision}
                </span>
                <div className="text-xs text-slate-500">
                  Gate {readinessPack.gate.id} | {readinessPack.gate.status}
                </div>
                <div className="text-3xl font-semibold text-slate-900">{readinessPack.summary.score}</div>
                <div className="text-xs uppercase tracking-wide text-slate-500">Readiness score</div>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-4">
              <div className="rounded border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs uppercase tracking-wide text-slate-500">Pending approvals</div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">{readinessPack.approvals.pending}</div>
              </div>
              <div className="rounded border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs uppercase tracking-wide text-slate-500">High-risk open</div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">{readinessPack.approvals.highRiskOpen}</div>
              </div>
              <div className="rounded border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs uppercase tracking-wide text-slate-500">Latest approval</div>
                <div className="mt-2 text-lg font-semibold text-slate-900">
                  {readinessPack.approvals.latestDecision?.toUpperCase() ?? "PENDING"}
                </div>
              </div>
              <div className="rounded border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs uppercase tracking-wide text-slate-500">Evidence refs</div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">{readinessPack.approvals.evidenceRefs.length}</div>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-[minmax(0,1.3fr)_minmax(280px,0.9fr)]">
              <div className="space-y-3">
                <div className="text-sm font-semibold text-slate-900">Go/no-go checklist</div>
                <div className="space-y-3">
                  {readinessPack.checklist.map((item) => (
                    <div key={item.id} className="rounded border border-slate-200 bg-slate-50 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="text-sm font-semibold text-slate-900">{item.label}</div>
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${checklistStatusBadgeClass(item.status)}`}>
                          {item.status.toUpperCase()}
                        </span>
                      </div>
                      <p className="mt-2 text-xs text-slate-600">{item.detail}</p>
                      {item.evidenceLinks.length ? (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {item.evidenceLinks.slice(0, 4).map((link) => (
                            <a
                              key={`${item.id}-${link}`}
                              className="text-[11px] font-medium text-blue-700 underline"
                              href={link}
                              target="_blank"
                              rel="noreferrer"
                            >
                              evidence
                            </a>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded border border-slate-200 bg-slate-50 p-3">
                  <div className="text-sm font-semibold text-slate-900">Evidence bundle</div>
                  <div className="mt-3 space-y-2 text-xs text-slate-700">
                    <a className="block font-medium text-blue-700 underline" href={readinessPack.evidence.readinessPackJson} target="_blank" rel="noreferrer">
                      readiness_pack.json
                    </a>
                    <a className="block font-medium text-blue-700 underline" href={readinessPack.evidence.readinessPackMarkdown} target="_blank" rel="noreferrer">
                      readiness_pack.md
                    </a>
                    <a className="block font-medium text-blue-700 underline" href={readinessPack.evidence.executivePackJson} target="_blank" rel="noreferrer">
                      executive_pack.json
                    </a>
                    <a className="block font-medium text-blue-700 underline" href={readinessPack.evidence.executivePackMarkdown} target="_blank" rel="noreferrer">
                      executive_pack.md
                    </a>
                    <a className="block font-medium text-blue-700 underline" href={readinessPack.evidence.controlTower} target="_blank" rel="noreferrer">
                      control_tower.json
                    </a>
                  </div>
                </div>

                <div className="rounded border border-slate-200 bg-slate-50 p-3">
                  <div className="text-sm font-semibold text-slate-900">Rollback playbook</div>
                  <div className="mt-2 text-xs text-slate-600">{readinessPack.rollback.strategy}</div>
                  <div className="mt-3 text-[11px] uppercase tracking-wide text-slate-500">Notes</div>
                  <ul className="mt-2 space-y-1 text-xs text-slate-700">
                    {readinessPack.rollback.notes.map((note) => (
                      <li key={note}>- {note}</li>
                    ))}
                  </ul>
                  <div className="mt-3 text-[11px] uppercase tracking-wide text-slate-500">Trigger signals</div>
                  <ul className="mt-2 space-y-1 text-xs text-slate-700">
                    {readinessPack.rollback.triggerSignals.map((signal) => (
                      <li key={signal}>- {signal}</li>
                    ))}
                  </ul>
                </div>

                <div className="rounded border border-slate-200 bg-slate-50 p-3">
                  <div className="text-sm font-semibold text-slate-900">Top risks</div>
                  <div className="mt-2 space-y-2">
                    {readinessPack.risks.slice(0, 4).map((risk) => (
                      <div key={risk.id} className="rounded border border-slate-200 bg-white p-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-xs font-semibold text-slate-900">{risk.title}</div>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${risk.level === "critical" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                            {risk.level.toUpperCase()}
                          </span>
                        </div>
                        <div className="mt-1 text-[11px] text-slate-600">{risk.mitigation}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded border border-slate-200 bg-white p-3">
              <div className="text-sm font-semibold text-slate-900">Next actions</div>
              <div className="mt-2 grid gap-2">
                {readinessPack.nextActions.map((action) => (
                  <div key={action} className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                    {action}
                  </div>
                ))}
              </div>
            </div>
          </Card>

          <Card className="border border-slate-200" bodyClassName="space-y-3">
            <div className="text-sm font-semibold text-slate-900">Review posture</div>
            <div className="grid gap-2 text-xs text-slate-700">
              <div>Project ID: {projectId}</div>
              <div>Latest approver: {readinessPack.approvals.latestApprover ?? "-"}</div>
              <div>Latest evidence ref: {readinessPack.approvals.latestEvidenceRef ?? "-"}</div>
              <div>Rollback evidence ready: {readinessPack.rollback.available ? "yes" : "no"}</div>
            </div>
            <div className="rounded border border-slate-200 bg-slate-50 p-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Validation steps</div>
              <ul className="mt-2 space-y-1 text-xs text-slate-700">
                {readinessPack.rollback.validationSteps.map((step) => (
                  <li key={step}>- {step}</li>
                ))}
              </ul>
            </div>
          </Card>
        </div>
      ) : null}

      {projectId && cutoverDashboard ? (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.85fr)]">
          <Card className="border border-slate-200" bodyClassName="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-1">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Cutover control plane</div>
                <div className="text-2xl font-semibold text-slate-900">{cutoverDashboard.summary.headline}</div>
                <p className="max-w-3xl text-sm text-slate-600">{cutoverDashboard.summary.rationale}</p>
              </div>
              <div className="flex flex-col items-end gap-2 text-right">
                <span
                  className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${cutoverDecisionBadgeClass(
                    cutoverDashboard.summary.decision,
                  )}`}
                >
                  {cutoverDashboard.summary.decision}
                </span>
                <div className="text-xs text-slate-500">Recommended phase: {cutoverDashboard.summary.recommendedPhase}</div>
                <div className="text-3xl font-semibold text-slate-900">{cutoverDashboard.summary.score}</div>
                <div className="text-xs uppercase tracking-wide text-slate-500">Cutover score</div>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-4">
              <div className="rounded border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs uppercase tracking-wide text-slate-500">Pass gates</div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">{cutoverDashboard.counts.pass}</div>
              </div>
              <div className="rounded border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs uppercase tracking-wide text-slate-500">Watch gates</div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">{cutoverDashboard.counts.watch}</div>
              </div>
              <div className="rounded border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs uppercase tracking-wide text-slate-500">Fail gates</div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">{cutoverDashboard.counts.fail}</div>
              </div>
              <div className="rounded border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs uppercase tracking-wide text-slate-500">Confidence</div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">
                  {Math.round(cutoverDashboard.summary.confidence * 100)}%
                </div>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {cutoverDashboard.gates.map((gate) => (
                <div key={gate.id} className="rounded border border-slate-200 bg-slate-50 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-slate-900">{gate.label}</div>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${cutoverGateBadgeClass(gate.status)}`}>
                      {gate.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">{gate.score}</div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-500">Gate score</div>
                  <p className="mt-2 text-xs text-slate-600">{gate.summary}</p>
                  {gate.blockers.length ? (
                    <div className="mt-2 space-y-1 text-[11px] text-slate-700">
                      {gate.blockers.slice(0, 3).map((blocker) => (
                        <div key={`${gate.id}-${blocker}`}>- {blocker}</div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>

            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.9fr)]">
              <div className="rounded border border-slate-200 bg-white p-3">
                <div className="text-sm font-semibold text-slate-900">Rollout plan</div>
                <div className="mt-2 text-xs text-slate-600">{cutoverDashboard.rolloutPlan.rationale}</div>
                <div className="mt-3 grid gap-2 text-xs text-slate-700 md:grid-cols-2">
                  <div>Current mode: {cutoverDashboard.rolloutPlan.currentMode}</div>
                  <div>Recommended phase: {cutoverDashboard.rolloutPlan.recommendedPhase}</div>
                  <div>Control tower: {cutoverDashboard.scorecard.controlTowerDecision}</div>
                  <div>Readiness: {cutoverDashboard.scorecard.readinessDecision}</div>
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-3">
                  <div className="rounded border border-slate-200 bg-slate-50 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">Stable review windows</div>
                    <div className="mt-2 text-2xl font-semibold text-slate-900">
                      {cutoverDashboard.rolloutHistory.stableReviewWindows}/{cutoverDashboard.rolloutHistory.requiredStableReviewWindows}
                    </div>
                  </div>
                  <div className="rounded border border-slate-200 bg-slate-50 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">Current rollout phase</div>
                    <div className="mt-2 text-lg font-semibold text-slate-900">{cutoverDashboard.scorecard.currentRolloutPhase}</div>
                  </div>
                  <div className="rounded border border-slate-200 bg-slate-50 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">Default promotion</div>
                    <div className="mt-2 text-lg font-semibold text-slate-900">
                      {cutoverDashboard.rolloutHistory.eligibleForDefaultPromotion ? "eligible" : "not yet"}
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-[11px] uppercase tracking-wide text-slate-500">Guardrails</div>
                <ul className="mt-2 space-y-1 text-xs text-slate-700">
                  {cutoverDashboard.rolloutPlan.guardrails.map((item) => (
                    <li key={item}>- {item}</li>
                  ))}
                </ul>
                <div className="mt-3 text-[11px] uppercase tracking-wide text-slate-500">Exit criteria</div>
                <ul className="mt-2 space-y-1 text-xs text-slate-700">
                  {cutoverDashboard.rolloutPlan.exitCriteria.map((item) => (
                    <li key={item}>- {item}</li>
                  ))}
                </ul>
                <div className="mt-3 text-[11px] uppercase tracking-wide text-slate-500">Rollout history</div>
                <div className="mt-2 space-y-2">
                  {cutoverDashboard.rolloutHistory.waves.length ? (
                    cutoverDashboard.rolloutHistory.waves.slice(0, 4).map((wave) => (
                      <div key={wave.id} className="rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="font-semibold text-slate-900">{wave.reviewWindowLabel}</div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${rolloutStatusBadgeClass(wave.status)}`}>
                              {wave.status.toUpperCase()}
                            </span>
                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${rolloutOutcomeBadgeClass(wave.outcome)}`}>
                              {wave.outcome.toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="mt-2 grid gap-1 md:grid-cols-2">
                          <div>Phase: {wave.phase}</div>
                          <div>Observed: {formatDateTime(wave.observedAt)}</div>
                          <div>Tenant count: {wave.tenantCount}</div>
                          <div>Cutover score: {wave.signals.cutoverScore}</div>
                        </div>
                        {wave.note ? <div className="mt-2 text-xs text-slate-600">{wave.note}</div> : null}
                      </div>
                    ))
                  ) : (
                    <div className="rounded border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-xs text-slate-600">
                      No rollout review window recorded yet. Start with a monitored pilot wave.
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                {rolloutState ? (
                  <div className="rounded border border-slate-200 bg-slate-50 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-semibold text-slate-900">Active tenant rollout</div>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tenantRolloutModeBadgeClass(rolloutState.mode)}`}>
                        {rolloutState.mode.toUpperCase()}
                      </span>
                    </div>
                    <div className="mt-3 grid gap-2 text-xs text-slate-700">
                      <div>Active path: {rolloutState.activePath}</div>
                      <div>Fallback enabled: {rolloutState.legacyFallbackEnabled ? "yes" : "no"}</div>
                      <div>Source: {rolloutState.source}</div>
                      <div>Updated: {formatDateTime(rolloutState.updatedAt)}</div>
                    </div>
                    {rolloutState.note ? (
                      <div className="mt-3 rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700">
                        {rolloutState.note}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {rolloutControl ? (
                  <div className="rounded border border-slate-200 bg-slate-50 p-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="space-y-1">
                        <div className="text-sm font-semibold text-slate-900">Operator rollout actions</div>
                        <div className="text-xs text-slate-600">{rolloutControl.summary.headline}</div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${releaseStatusBadgeClass(rolloutControl.summary.releaseStatus)}`}>
                          {rolloutControl.summary.releaseStatus.toUpperCase()}
                        </span>
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${cutoverDecisionBadgeClass(rolloutControl.summary.cutoverDecision)}`}>
                          {rolloutControl.summary.cutoverDecision}
                        </span>
                      </div>
                    </div>
                    <div className="mt-3 text-xs text-slate-700">{rolloutControl.summary.rationale}</div>

                    {rolloutControl.recommendedAction ? (
                      <div className="mt-3 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                        Recommended move: <span className="font-semibold">{rolloutControl.recommendedAction.label}</span>
                        {" "}
                        {"->"} {rolloutControl.recommendedAction.nextMode.toUpperCase()}
                      </div>
                    ) : null}

                    <div className="mt-3 grid gap-2">
                      {rolloutControl.operatorGuidance.map((entry) => (
                        <div key={entry} className="rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700">
                          {entry}
                        </div>
                      ))}
                    </div>

                    <div className="mt-3 grid gap-3 text-xs text-slate-700">
                      <label className="space-y-1">
                        <span className="font-semibold text-slate-800">Operator</span>
                        <input
                          className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                          type="text"
                          value={tenantRolloutDecider}
                          onChange={(event) => setTenantRolloutDecider(event.target.value)}
                          placeholder="release-board"
                        />
                      </label>
                      <label className="space-y-1">
                        <span className="font-semibold text-slate-800">Evidence references</span>
                        <input
                          className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                          type="text"
                          value={tenantRolloutEvidenceRefs}
                          onChange={(event) => setTenantRolloutEvidenceRefs(event.target.value)}
                          placeholder="CAB-99, OPS-RELEASE-42"
                        />
                      </label>
                      <label className="space-y-1">
                        <span className="font-semibold text-slate-800">Operator note</span>
                        <textarea
                          className="min-h-[64px] w-full rounded border border-slate-300 px-2 py-1 text-xs"
                          value={tenantRolloutNote}
                          onChange={(event) => setTenantRolloutNote(event.target.value)}
                          placeholder="Record the rationale for this transition or rollback."
                        />
                      </label>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {rolloutControl.allowedActions.map((action) => (
                        <Button
                          key={action.action}
                          size="sm"
                          variant={action.recommended ? "primary" : "secondary"}
                          onClick={() => void submitRolloutAction(action.action)}
                          disabled={operatorActionDisabled}
                        >
                          {isSubmitting ? "Submitting..." : action.label}
                        </Button>
                      ))}
                    </div>
                    <div className="mt-2 text-[11px] text-slate-500">
                      Operator and evidence references are required before any rollout transition is recorded.
                    </div>

                    <div className="mt-4 space-y-2">
                      <div className="text-[11px] uppercase tracking-wide text-slate-500">Transition history</div>
                      {rolloutControl.transitionHistory.length ? (
                        rolloutControl.transitionHistory.slice(0, 4).map((transition) => (
                          <div key={transition.id} className="rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="font-semibold text-slate-900">
                                {transition.previousMode.toUpperCase()} {"->"} {transition.nextMode.toUpperCase()}
                              </div>
                              <div className="text-[11px] text-slate-500">{formatDateTime(transition.occurredAt)}</div>
                            </div>
                            <div className="mt-1">
                              {transition.action} by {transition.decidedBy}
                              {" "}
                              ({transition.cutoverDecision}, {transition.releaseStatus.toUpperCase()})
                            </div>
                            {transition.note ? <div className="mt-1 text-[11px] text-slate-600">{transition.note}</div> : null}
                          </div>
                        ))
                      ) : (
                        <div className="rounded border border-dashed border-slate-300 bg-white px-3 py-2 text-xs text-slate-600">
                          No operator transition has been recorded yet.
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}

                <div className="rounded border border-slate-200 bg-slate-50 p-3">
                  <div className="text-sm font-semibold text-slate-900">Cutover artifacts</div>
                  <div className="mt-3 space-y-2 text-xs text-slate-700">
                    <a className="block font-medium text-blue-700 underline" href={cutoverDashboard.artifacts.cutoverDashboardJson} target="_blank" rel="noreferrer">
                      cutover_dashboard.json
                    </a>
                    <a className="block font-medium text-blue-700 underline" href={cutoverDashboard.artifacts.cutoverDashboardMarkdown} target="_blank" rel="noreferrer">
                      cutover_dashboard.md
                    </a>
                    <a className="block font-medium text-blue-700 underline" href={cutoverDashboard.artifacts.readinessPackMarkdown} target="_blank" rel="noreferrer">
                      readiness_pack.md
                    </a>
                    <a className="block font-medium text-blue-700 underline" href={cutoverDashboard.artifacts.executivePackMarkdown} target="_blank" rel="noreferrer">
                      executive_pack.md
                    </a>
                    {releaseBundle ? (
                      <>
                        <a className="block font-medium text-blue-700 underline" href={releaseBundle.artifacts.releaseBundleJson} target="_blank" rel="noreferrer">
                          release_bundle.json
                        </a>
                        <a className="block font-medium text-blue-700 underline" href={releaseBundle.artifacts.releaseBundleMarkdown} target="_blank" rel="noreferrer">
                          release_bundle.md
                        </a>
                      </>
                    ) : null}
                  </div>
                </div>

                <div className="rounded border border-slate-200 bg-slate-50 p-3">
                  <div className="text-sm font-semibold text-slate-900">Next rollout actions</div>
                  <div className="mt-2 grid gap-2">
                    {cutoverDashboard.nextActions.map((action) => (
                      <div key={action} className="rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700">
                        {action}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <Card className="border border-slate-200" bodyClassName="space-y-3">
            <div className="text-sm font-semibold text-slate-900">Rollout scorecard</div>
            <div className="grid gap-2 text-xs text-slate-700">
              <div>Latest run status: {cutoverDashboard.scorecard.latestRunStatus ?? "-"}</div>
              <div>Deploy candidate: {cutoverDashboard.scorecard.deployCandidateRunId ?? "-"}</div>
              <div>Pending approvals: {cutoverDashboard.scorecard.pendingApprovals}</div>
              <div>High-risk open: {cutoverDashboard.scorecard.highRiskOpen}</div>
              <div>Stable review windows: {cutoverDashboard.scorecard.stableReviewWindows}</div>
            </div>
            {releaseBundle ? (
              <div className="rounded border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-slate-900">Release bundle</div>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${releaseStatusBadgeClass(releaseBundle.summary.releaseStatus)}`}>
                    {releaseBundle.summary.releaseStatus.toUpperCase()}
                  </span>
                </div>
                <div className="mt-2 text-xs text-slate-700">{releaseBundle.summary.headline}</div>
                <div className="mt-2 text-[11px] text-slate-600">{releaseBundle.summary.rationale}</div>
              </div>
            ) : null}
            <div className="rounded border border-slate-200 bg-slate-50 p-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Rollback switch</div>
              <div className="mt-2 text-xs text-slate-700">{cutoverDashboard.rolloutPlan.rollbackSwitch}</div>
            </div>
            <div className="rounded border border-slate-200 bg-slate-50 p-3">
              <div className="text-sm font-semibold text-slate-900">Emergency rollout override</div>
              <div className="mt-1 text-[11px] text-slate-500">
                Use only when the operator action rail above is insufficient. The same operator and evidence references are reused.
              </div>
              <div className="mt-3 grid gap-3 text-xs text-slate-700">
                <label className="space-y-1">
                  <span className="font-semibold text-slate-800">Mode</span>
                  <select
                    className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                    value={tenantRolloutMode}
                    onChange={(event) => setTenantRolloutMode(event.target.value as FoundryV2TenantRolloutMode)}
                  >
                    <option value="disabled">DISABLED</option>
                    <option value="pilot">PILOT</option>
                    <option value="staged">STAGED</option>
                    <option value="default">DEFAULT</option>
                    <option value="legacy-fallback-only">LEGACY_FALLBACK_ONLY</option>
                  </select>
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-700">
                  <input
                    checked={tenantRolloutFallbackEnabled}
                    onChange={(event) => setTenantRolloutFallbackEnabled(event.target.checked)}
                    type="checkbox"
                  />
                  Keep legacy fallback enabled
                </label>
                <div className="rounded border border-slate-200 bg-white px-3 py-2 text-[11px] text-slate-600">
                  Operator: {tenantRolloutDecider.trim() || "-"} | Evidence refs: {operatorEvidenceRefs.length}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" onClick={() => void submitTenantRolloutState()} disabled={isSubmitting || isLoading}>
                    {isSubmitting ? "Submitting..." : "Apply emergency override"}
                  </Button>
                </div>
              </div>
            </div>
            <div className="rounded border border-slate-200 bg-slate-50 p-3">
              <div className="text-sm font-semibold text-slate-900">Record rollout review window</div>
              <div className="mt-3 grid gap-3 text-xs text-slate-700">
                <label className="space-y-1">
                  <span className="font-semibold text-slate-800">Phase</span>
                  <select
                    className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                    value={rolloutPhase}
                    onChange={(event) => setRolloutPhase(event.target.value as FoundryV2RolloutWavePhase)}
                  >
                    <option value="pilot">PILOT</option>
                    <option value="staged">STAGED</option>
                    <option value="default">DEFAULT</option>
                  </select>
                </label>
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="space-y-1">
                    <span className="font-semibold text-slate-800">Status</span>
                    <select
                      className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                      value={rolloutStatus}
                      onChange={(event) => setRolloutStatus(event.target.value as FoundryV2RolloutWaveStatus)}
                    >
                      <option value="planned">PLANNED</option>
                      <option value="monitoring">MONITORING</option>
                      <option value="completed">COMPLETED</option>
                      <option value="rolled_back">ROLLED_BACK</option>
                    </select>
                  </label>
                  <label className="space-y-1">
                    <span className="font-semibold text-slate-800">Outcome</span>
                    <select
                      className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                      value={rolloutOutcome}
                      onChange={(event) => setRolloutOutcome(event.target.value as FoundryV2RolloutWaveOutcome)}
                    >
                      <option value="stable">STABLE</option>
                      <option value="watch">WATCH</option>
                      <option value="failed">FAILED</option>
                      <option value="rolled_back">ROLLED_BACK</option>
                    </select>
                  </label>
                </div>
                <label className="space-y-1">
                  <span className="font-semibold text-slate-800">Review window label</span>
                  <input
                    className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                    type="text"
                    value={rolloutLabel}
                    onChange={(event) => setRolloutLabel(event.target.value)}
                    placeholder="Pilot review window 1"
                  />
                </label>
                <label className="space-y-1">
                  <span className="font-semibold text-slate-800">Tenant IDs</span>
                  <input
                    className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                    type="text"
                    value={rolloutTenants}
                    onChange={(event) => setRolloutTenants(event.target.value)}
                    placeholder="tenant-a, tenant-b"
                  />
                </label>
                <label className="space-y-1">
                  <span className="font-semibold text-slate-800">Evidence references</span>
                  <input
                    className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                    type="text"
                    value={rolloutEvidenceRefs}
                    onChange={(event) => setRolloutEvidenceRefs(event.target.value)}
                    placeholder="CAB-99, KPI-REPORT-2026-W14"
                  />
                </label>
                <label className="space-y-1">
                  <span className="font-semibold text-slate-800">Rollout note</span>
                  <textarea
                    className="min-h-[64px] w-full rounded border border-slate-300 px-2 py-1 text-xs"
                    value={rolloutNote}
                    onChange={(event) => setRolloutNote(event.target.value)}
                    placeholder="Summarize the observed rollout posture for this review window."
                  />
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" onClick={() => void submitRolloutWave()} disabled={isSubmitting || isLoading}>
                    {isSubmitting ? "Submitting..." : "Record rollout review window"}
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      ) : null}

      <Card className="border border-slate-200" bodyClassName="space-y-3">
        {!projectId ? (
          <div className="text-sm text-amber-700">No project selected. Open `/jarvis-v2` or `/foundry-v2` with `projectId` first.</div>
        ) : isLoading && !latestHandoff && !error ? (
          <div className="text-sm text-slate-600">
            Loading the Foundry V2 governance snapshot for project `{projectId}`.
          </div>
        ) : !latestHandoff ? (
          <div className="text-sm text-amber-700">No Foundry V2 handoff available for project `{projectId}`.</div>
        ) : (
          <>
            <div className="grid gap-2 text-xs text-slate-700 md:grid-cols-2">
              <div>Project ID: {projectId}</div>
              <div>Handoff ID: {latestHandoff.id}</div>
              <div>Runs loaded: {runs.length}</div>
              <div>Pending high-risk approvals: {pendingHighRiskRuns.length}</div>
            </div>

            {runs.length ? (
              <div className="space-y-3 rounded border border-slate-200 bg-slate-50 p-3">
                <div className="grid gap-3 text-xs text-slate-700 md:grid-cols-2">
                  <label className="space-y-1">
                    <span className="font-semibold text-slate-800">Target run</span>
                    <select
                      className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                      value={selectedRunId}
                      onChange={(event) => setSelectedRunId(event.target.value)}
                    >
                      {runs.map((run) => (
                        <option key={run.id} value={run.id}>
                          {run.id} | {run.status.toUpperCase()} | {run.approval.required ? "approval-required" : "no-approval"}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="space-y-1">
                    <span className="font-semibold text-slate-800">Decision</span>
                    <select
                      className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                      value={decision}
                      onChange={(event) => setDecision(event.target.value as FoundryV2GovernanceApprovalDecision)}
                    >
                      <option value="approved">APPROVED</option>
                      <option value="rejected">REJECTED</option>
                    </select>
                  </label>
                </div>

                <div className="grid gap-3 text-xs text-slate-700 md:grid-cols-2">
                  <label className="space-y-1">
                    <span className="font-semibold text-slate-800">Approver</span>
                    <input
                      className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                      type="text"
                      value={approver}
                      onChange={(event) => setApprover(event.target.value)}
                      placeholder="name or role"
                    />
                  </label>
                  <label className="space-y-1">
                    <span className="font-semibold text-slate-800">Evidence reference</span>
                    <input
                      className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                      type="text"
                      value={evidenceRef}
                      onChange={(event) => setEvidenceRef(event.target.value)}
                      placeholder="ticket id or URL"
                    />
                  </label>
                </div>

                <label className="space-y-1 text-xs">
                  <span className="font-semibold text-slate-800">Note</span>
                  <textarea
                    className="min-h-[64px] w-full rounded border border-slate-300 px-2 py-1 text-xs"
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    placeholder="Optional decision rationale"
                  />
                </label>

                <div className="grid gap-1 text-[11px] text-slate-600">
                  <div>
                    Run requires approval: {selectedRun?.approval.required ? "yes" : "no"} | high-risk stages:{" "}
                    {selectedRun?.executionGraph.highRiskStageIds.length
                      ? selectedRun.executionGraph.highRiskStageIds.join(", ")
                      : "-"}
                  </div>
                  {latestApproval ? (
                    <div>
                      <span className={`inline-flex rounded-full px-2 py-0.5 font-semibold ${approvalStatusBadgeClass(latestApproval.status)}`}>
                        {latestApproval.status.toUpperCase()}
                      </span>{" "}
                      <span className={`inline-flex rounded-full px-2 py-0.5 font-semibold ${escalationBadgeClass(latestApproval.escalation)}`}>
                        SLA {latestApproval.escalation.toUpperCase()}
                      </span>{" "}
                      {latestApproval.approver ? `by ${latestApproval.approver}` : "awaiting decision"} | requested{" "}
                      {formatDateTime(latestApproval.requestedAt)} | deadline {formatDateTime(latestApproval.deadlineAt)} |{" "}
                      {resolveLiveSla(latestApproval, clockMs).overdueMs > 0
                        ? `overdue ${formatDuration(resolveLiveSla(latestApproval, clockMs).overdueMs)}`
                        : `remaining ${formatDuration(resolveLiveSla(latestApproval, clockMs).remainingMs)}`}
                    </div>
                  ) : (
                    <div>No decision recorded yet for selected run.</div>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" onClick={() => void submitApproval()} disabled={isSubmitting || isLoading}>
                    {isSubmitting ? "Submitting..." : "Record Governance V2 decision"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-xs text-slate-600">No run found for this handoff.</div>
            )}

            {approvalHistory.length ? (
              <div className="rounded border border-slate-200 bg-white p-2">
                <div className="text-xs font-semibold text-slate-800">Approval history (selected scope)</div>
                <div className="mt-2 space-y-2">
                  {approvalHistory.slice(0, 6).map((approval) => (
                    <div key={approval.id} className="rounded border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-700">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono">{approval.runId}</span>
                        <span className={`rounded-full px-2 py-0.5 font-semibold ${approvalStatusBadgeClass(approval.status)}`}>
                          {approval.status.toUpperCase()}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <span className={`rounded-full px-2 py-0.5 font-semibold ${escalationBadgeClass(approval.escalation)}`}>
                          SLA {approval.escalation.toUpperCase()}
                        </span>
                        <span>risk {approval.riskTier}</span>
                        <span>policy {approval.policyHours}h</span>
                        <span>deadline {formatDateTime(approval.deadlineAt)}</span>
                        {resolveLiveSla(approval, clockMs).overdueMs > 0 ? (
                          <span>overdue {formatDuration(resolveLiveSla(approval, clockMs).overdueMs)}</span>
                        ) : (
                          <span>remaining {formatDuration(resolveLiveSla(approval, clockMs).remainingMs)}</span>
                        )}
                      </div>
                      <div className="mt-1">
                        {approval.approver ?? "no approver yet"} | decided {formatDateTime(approval.decidedAt)} | requested{" "}
                        {formatDateTime(approval.requestedAt)}
                      </div>
                      <div className="mt-1 break-all">Evidence: {approval.evidenceRef ?? "-"}</div>
                      {approval.note ? <div className="mt-1 break-all">Note: {approval.note}</div> : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </>
        )}

        {error ? <div className="text-sm text-red-700">{error}</div> : null}
        {success ? <div className="text-sm text-emerald-700">{success}</div> : null}
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        {CONTROL_ROWS.map((row) => (
          <Card key={row.control} className="border border-slate-200" bodyClassName="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold text-slate-900">{row.control}</div>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                  row.status === "active"
                    ? "bg-emerald-100 text-emerald-700"
                    : "bg-amber-100 text-amber-700"
                }`}
              >
                {row.status.toUpperCase()}
              </span>
            </div>
            <p className="text-sm text-slate-600">{row.detail}</p>
          </Card>
        ))}
      </div>
      </div>
    </FoundryV2AccessGuard>
  );
}
